// window.storage shim — replaces the localStorage shim that lived in the old
// inlined <script> at the top of index.html. Talks to the backend storage-
// compat endpoint at `${API_BASE}/storage/:key`.
//
// The legacy `loadS`/`saveS` pair in App.jsx (lines 17–18) expects:
//   - get(key)  → { value: <JSON-stringified-payload> }  (or null on 404)
//   - set(key, value) → fire-and-forget
//
// We preserve that exact shape so App.jsx needs zero edits.

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

// Per-key debounce — saveS fires on every keystroke via useEffect, which would
// otherwise hammer the backend. Coalesce calls per-key within DEBOUNCE_MS into
// a single PUT carrying the latest value.
const DEBOUNCE_MS = 500;
const pendingTimers = new Map();
const pendingValues = new Map();

async function rawGet(key) {
  const r = await fetch(`${API_BASE}/storage/${encodeURIComponent(key)}`, {
    credentials: 'include',
  });
  if (r.status === 401) {
    // Auth required — bounce through the AuthGate by reloading. AuthGate
    // listens to /api/auth/me and will render the login screen.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ir:auth-expired'));
    }
    return null;
  }
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`storage.get(${key}): HTTP ${r.status}`);

  const data = await r.json();
  // Backend returns the parsed JSON object/array. The legacy loadS does
  // `JSON.parse(r.value)`, so we hand back the JSON-stringified form.
  return { value: JSON.stringify(data) };
}

async function rawSet(key, bodyString) {
  const r = await fetch(`${API_BASE}/storage/${encodeURIComponent(key)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: bodyString,
  });
  if (r.status === 401) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ir:auth-expired'));
    }
    return null;
  }
  if (!r.ok) {
    // Don't throw — saveS swallows errors. Log so dev catches it.
    // eslint-disable-next-line no-console
    console.warn(`storage.set(${key}): HTTP ${r.status}`);
  }
  return null;
}

function debouncedSet(key, value) {
  // saveS hands us an already-stringified JSON value. Send it verbatim so the
  // backend receives a real JSON document.
  const bodyString = typeof value === 'string' ? value : JSON.stringify(value);
  pendingValues.set(key, bodyString);

  const existing = pendingTimers.get(key);
  if (existing) clearTimeout(existing);

  const t = setTimeout(() => {
    const body = pendingValues.get(key);
    pendingTimers.delete(key);
    pendingValues.delete(key);
    rawSet(key, body).catch(() => {
      /* swallow — matches legacy saveS behaviour */
    });
  }, DEBOUNCE_MS);
  pendingTimers.set(key, t);
}

// Public API matching the legacy window.storage shape.
export const storage = {
  async get(key) {
    return rawGet(key);
  },
  async set(key, value) {
    debouncedSet(key, value);
  },
};

// Flush any pending writes if the user navigates away.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    for (const [key, body] of pendingValues.entries()) {
      // navigator.sendBeacon is the best-effort flush on unload.
      try {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${API_BASE}/storage/${encodeURIComponent(key)}`, blob);
      } catch {
        /* ignore */
      }
    }
  });
}
